import { useEffect, useRef, useState } from 'react';
import { axiosCaller } from '../../../utils/apiCaller';

export interface PulseMetric {
    current: number | null;
    previous: number | null;
    delta_pct: number | null;
}

export interface RevenueMonthPoint {
    month: string;        // "YYYY-MM-01"
    revenue: number;
    agreement_count: number;
}

export interface StatusMatrixSegment {
    key: string;
    label: string;       // English from backend — re-translate at render boundary
    count: number;
    color: string;       // hex; works on light surfaces
}

export interface StatusHighlight {
    format: 'money' | 'percent' | 'number';
    key: string;         // i18n key suffix under analyticsV2.entityGrid.metric.*
    value: number;
}

export interface StatusMatrixEntry {
    entity: string;
    label: string;
    total: number;
    statuses: StatusMatrixSegment[];
    highlight?: StatusHighlight | null; // backend supplies money metrics; FE derives the rest
}

export interface TopClient {
    client_id: number;
    name: string;
    city: string | null;
    revenue: number;
    agreement_count: number;
}

export interface BottleneckEntry {
    responsibility_id: number;
    name: string;
    total_reworks: number;
    affected_products: number;
}

export interface FunnelStage {
    responsibility_id: number;
    name: string;
    reached: number;
    completed: number;
    reworked: number;
    avg_sequence: number;
}

export interface CycleTimeDistribution {
    responsibility_id: number;
    name: string;
    sample_size: number;
    p25: number | null;
    p50: number | null;
    p75: number | null;
    p95: number | null;
    min: number | null;
    max: number | null;
}

export interface CashflowSnapshot {
    total_value: number;
    collected: number;
    outstanding: number;
    invoiced: number;
    collection_rate_pct: number | null;
    agreement_count: number;
}

export interface SalesFunnelStage {
    key: 'measures' | 'offers' | 'agreements';
    count: number;
    pct_of_top: number | null;
}

export interface SalesFunnelData {
    stages: SalesFunnelStage[];
    close_rate_pct: number | null;
}

export interface AgingProduct {
    status_id: number;
    product_id: number;
    responsibility_id: number | null;
    responsibility_name: string;
    article_name: string;
    client_name: string;
    client_id: number | null;
    started_at: string | null;
    stuck_seconds: number;
}

export interface HeatmapCell {
    dow: number;     // 0=Monday … 6=Sunday
    hour: number;    // 0–23
    count: number;
}

export interface DurationSummary {
    sample_size: number;
    p25: number | null;
    p50: number | null;
    p75: number | null;
    p95: number | null;
    min: number | null;
    max: number | null;
}

export interface ProductionBandBreakdown {
    done: number;         // every stage in the band complete
    in_progress: number;  // a stage in-process (status 4)
    waiting: number;      // a stage waiting (status 2), nothing in-process/late
    late: number;         // has an overdue stage (status 3)
}

export interface ProductionPipeline {
    total: number;                        // windowed: products with any production activity in the period
    completed: number;                    // windowed: production_completed_at within the range
    planning: ProductionBandBreakdown;    // category=activity (planning/design), by status
    production: ProductionBandBreakdown;  // category=production (manufacturing), by status
}

export interface OverviewData {
    pulse: {
        revenue: PulseMetric;
        active_pipeline_count: PulseMetric;
        avg_cycle_time_seconds: PulseMetric;
        rework_rate_pct: PulseMetric;
        completions_count: PulseMetric;
    };
    production_pipeline: ProductionPipeline | null;
    revenue_by_month: RevenueMonthPoint[];
    revenue_by_month_previous: RevenueMonthPoint[] | null;
    status_matrix: StatusMatrixEntry[];
    status_matrix_previous: StatusMatrixEntry[] | null;
    top_clients: TopClient[];
    top_clients_previous: TopClient[] | null;
    rework_leaderboard: BottleneckEntry[];
    rework_leaderboard_previous: BottleneckEntry[] | null;
    funnel: FunnelStage[];
    funnel_previous: FunnelStage[] | null;
    cycle_time_distributions: CycleTimeDistribution[];
    // Nullable so the type holds even if a stale (pre-cashflow-deploy) cache
    // entry hydrates without the field. AnalyticsPageV2 fallbacks to null.
    cashflow: CashflowSnapshot | null;
    cashflow_previous: CashflowSnapshot | null;
    sales_funnel: SalesFunnelData | null;
    sales_funnel_previous: SalesFunnelData | null;
    aging_products: AgingProduct[] | null;
    activity_heatmap: HeatmapCell[] | null;
    lead_time: DurationSummary | null;
    lead_time_previous: DurationSummary | null;
    generated_at: string;
    range: {
        from: string;
        to: string;
        compare_from: string | null;
        compare_to: string | null;
    };
}

export interface DateRange {
    from: Date;
    to: Date;
    compareFrom?: Date;
    compareTo?: Date;
}

export interface OverviewFilters {
    clientIds?: number[];
    cities?: string[];
    responsibilityIds?: number[];
}

export interface OverviewParams extends DateRange {
    filters?: OverviewFilters;
}

const toYmd = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/**
 * Fetches /analytics/overview for the given range + filters. Re-runs when any
 * input identity changes. Returns { data, loading, error }.
 *
 * Pass `pollMs` to silently re-fetch on an interval. The response is only
 * pushed to state when its `generated_at` differs from the rendered snapshot,
 * so a poll that hits the warm server-side cache causes no re-render or KPI
 * re-animation. This is what makes the "Updated Ns ago" LiveIndicator honest:
 * the data actually refreshes within `pollMs` of the server cache expiring,
 * instead of the indicator counting up forever on an idle tab.
 */
export function useOverviewData(params: OverviewParams, pollMs?: number) {
    const [data, setData] = useState<OverviewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // generated_at of the snapshot currently in state — lets a poll skip the
    // setData (and the consequent re-render) when the server returns the same
    // cached snapshot.
    const generatedAtRef = useRef<string | null>(null);

    const fromKey = params.from.getTime();
    const toKey = params.to.getTime();
    const cfKey = params.compareFrom?.getTime() ?? 0;
    const ctKey = params.compareTo?.getTime() ?? 0;
    const clientKey = (params.filters?.clientIds ?? []).slice().sort().join(',');
    const cityKey = (params.filters?.cities ?? []).slice().sort().join('|');
    const respKey = (params.filters?.responsibilityIds ?? []).slice().sort().join(',');

    useEffect(() => {
        let cancelled = false;

        const buildUrl = () => {
            const qs = new URLSearchParams({
                from: toYmd(params.from),
                to: toYmd(params.to),
            });
            if (params.compareFrom && params.compareTo) {
                qs.set('compare_from', toYmd(params.compareFrom));
                qs.set('compare_to', toYmd(params.compareTo));
            }
            if (clientKey) qs.set('client_ids', clientKey);
            if (cityKey) qs.set('cities', cityKey.replace(/\|/g, ','));
            if (respKey) qs.set('responsibility_ids', respKey);
            return `analytics/overview?${qs.toString()}`;
        };

        // isPoll=false → foreground fetch (shows skeleton, surfaces errors).
        // isPoll=true  → background refresh (no skeleton, swallow transient
        //                errors, update state only when the snapshot changed).
        const fetchOnce = (isPoll: boolean) => {
            if (!isPoll) {
                setLoading(true);
                setError(null);
            }
            return axiosCaller('GET', buildUrl())
                .then((res: any) => {
                    if (cancelled) return;
                    const next = (res?.data ?? null) as OverviewData | null;
                    const nextGen = next?.generated_at ?? null;
                    if (isPoll && nextGen !== null && nextGen === generatedAtRef.current) {
                        return; // same cached snapshot — no UI churn
                    }
                    generatedAtRef.current = nextGen;
                    setData(next);
                    if (!isPoll) setLoading(false);
                    else setError(null); // a good poll clears a prior transient error
                })
                .catch((err: any) => {
                    if (cancelled) return;
                    if (isPoll) return; // keep last good data on a transient poll failure
                    const status = err?.response?.status;
                    if (status === 403) setError('permission_denied');
                    else if (status === 401) setError('unauthenticated');
                    else setError(err?.response?.data?.message || 'load_failed');
                    setLoading(false);
                });
        };

        fetchOnce(false);

        let intervalId: ReturnType<typeof setInterval> | null = null;
        if (pollMs && pollMs > 0) {
            intervalId = setInterval(() => fetchOnce(true), pollMs);
        }

        return () => {
            cancelled = true;
            if (intervalId !== null) clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromKey, toKey, cfKey, ctKey, clientKey, cityKey, respKey, pollMs]);

    return { data, loading, error };
}
